// processSnapshot.jsx
//
// "Process Snapshot" tab in the AI Consultant dashboard.
// Renders the same HTML report used by the AppNav export, live inside an iframe,
// with a download button so clients can take it away.
//
// Data is read from the in-memory process miner arrays — no backend round-trip.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, RefreshCw, LayoutDashboard } from 'lucide-react';
import ConsultantTabHeader, { getHeaderPrimaryButtonStyle, getHeaderSecondaryButtonStyle } from '@process-advisor/process-miner/consultant/ConsultantTabHeader';
import {
  generateExportData,
  getActivityArray,
  getEventLogArray,
  getModelID,
  getModelMetricsTotal,
  getProcessModelTitle,
  getStepArray,
  getTaskArray,
  getVariantArrayUnfiltered,
} from '@process-advisor/process-miner/processMiner/processMiner';

// ── lazy import so the bundle only loads when the tab is first visited ────────
const loadGenerator = () => import('@process-advisor/lib/htmlReportGenerator').then(m => m.generateProcessReportHTML);

// ── helpers ───────────────────────────────────────────────────────────────────
const buildHtml = async (processLabel, entityLabel) => {
  const generateProcessReportHTML = await loadGenerator();

  const exportData = generateExportData({
    activityArray: getActivityArray(),
    stepArray:     getStepArray(),
    variantArray:  getVariantArrayUnfiltered(),
    taskArray:     getTaskArray(),
    eventLogArray: getEventLogArray(),
  });
  const { variants, steps, diagramData } = exportData;

  const issues = getTaskArray().map(t => ({
    key:      t.task      || '',
    summary:  t.summary   || '',
    type:     t.issueType || t.type || '',
    status:   t.status    || '',
    created:  t.created   || '',
    resolved: t.resolved  || '',
    duration: t.duration  || 0,
  }));

  const modelName = getProcessModelTitle() || 'Report';
  const model     = { name: modelName, id: getModelID() };

  return {
    html: generateProcessReportHTML(
      model, variants, steps, issues, diagramData,
      undefined, processLabel, entityLabel,
      [], [], [],
      getModelMetricsTotal(),
    ),
    modelName,
  };
};

const downloadHtml = (html, modelName) => {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${modelName.replace(/[^a-z0-9]/gi, '_')}-process-snapshot-${new Date().toISOString().slice(0, 10)}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ── component ─────────────────────────────────────────────────────────────────
const ProcessSnapshot = ({ processLabel = 'Process', entityLabel = 'Work Item', refreshKey }) => {
  const [html,      setHtml]      = useState(null);
  const [modelName, setModelName] = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [generated, setGenerated] = useState(false);
  const iframeRef = useRef(null);

  const generate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { html: h, modelName: n } = await buildHtml(processLabel, entityLabel);
      setHtml(h);
      setModelName(n);
      setGenerated(true);
    } catch (err) {
      console.error('[ProcessSnapshot] generation failed:', err);
      setError('Failed to generate the snapshot. Make sure a process model is loaded.');
    } finally {
      setLoading(false);
    }
  }, [processLabel, entityLabel]);

  // Write HTML into the iframe whenever it changes
  useEffect(() => {
    if (!html || !iframeRef.current) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
  }, [html]);

  // Auto-generate on first mount (or when refreshKey changes if already generated)
  useEffect(() => {
    generate();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: '#F4F5F7', overflow: 'hidden',
    }}>

      {/* Outer card — matches the Consulting Report bordered card pattern */}
      <div style={{
        display: 'flex', flexDirection: 'column',
        flex: 1, minHeight: 0,
        margin: '10px 10px 10px',
        border: '2px solid #dfe1e6',
        borderRadius: '3px',
        boxShadow: '0 1px 3px rgba(9,45,66,0.08)',
        overflow: 'hidden',
      }}>

        {/* Tab header */}
        <ConsultantTabHeader
          title="Process Snapshot"
          subtitle="Live evidence view — metrics dashboard, process flow, variants & transitions"
          icon={<LayoutDashboard size={20} color="white" />}
          iconBackground="linear-gradient(135deg, #0052CC 0%, #2684FF 100%)"
          iconShadowColor="rgba(0,82,204,0.25)"
          titleColor="#0052CC"
          actions={(
            <>
              <button
                onClick={generate}
                disabled={loading}
                style={{ ...getHeaderSecondaryButtonStyle(), opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer', gap: 6 }}
              >
                <RefreshCw size={13} style={{ animation: loading ? 'spin 0.8s linear infinite' : 'none' }} />
                {loading ? 'Generating…' : 'Refresh'}
              </button>

              {html && (
                <button
                  onClick={() => downloadHtml(html, modelName)}
                  style={{ ...getHeaderPrimaryButtonStyle('#0052CC'), gap: 6 }}
                >
                  <Download size={13} />
                  Download HTML
                </button>
              )}
            </>
          )}
        />

        {/* Content area */}
        <div style={{ flex: 1, minHeight: 0, position: 'relative', background: '#fff' }}>

          {/* Loading overlay */}
          {loading && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 10,
              background: 'rgba(244,245,247,0.85)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 14,
            }}>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <div style={{
                width: 36, height: 36,
                border: '3px solid #DFE1E6', borderTopColor: '#0052CC',
                borderRadius: '50%', animation: 'spin 0.7s linear infinite',
              }} />
              <div style={{ fontSize: 13, color: '#42526E', fontWeight: 600 }}>
                Building snapshot…
              </div>
            </div>
          )}

          {/* Error state */}
          {error && !loading && (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', gap: 12, padding: 32,
            }}>
              <div style={{
                padding: '14px 20px', borderRadius: 8,
                background: '#FFEBE6', border: '1px solid #FF8F73',
                fontSize: 13, color: '#BF2600', maxWidth: 500, textAlign: 'center',
              }}>
                {error}
              </div>
              <button
                onClick={generate}
                style={{
                  padding: '8px 18px', borderRadius: 6,
                  border: '1px solid #DFE1E6', background: '#fff',
                  fontSize: 13, fontWeight: 600, color: '#172B4D', cursor: 'pointer',
                }}
              >
                Try Again
              </button>
            </div>
          )}

          {/* Iframe */}
          <iframe
            ref={iframeRef}
            title="Process Snapshot"
            style={{
              width: '100%', height: '100%',
              border: 'none',
              display: (loading || error) ? 'none' : 'block',
            }}
            sandbox="allow-scripts allow-same-origin"
          />
        </div>

      </div>
    </div>
  );
};

export default ProcessSnapshot;
